import { Space, Tooltip, Affix } from "antd";
import React, { ReactNode } from "react";
import { enmValidationLevel, ICallOptions, IResult } from "./models";
import { Redirect } from "react-router-dom";
import AppContext, { AppContextProps } from "./appContext";
import { Core } from "./core";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import PageHeader from "../pageHeader";

export interface IBaseProps {
    onClose?: (sender: any) => void;
    isLookupMode?: boolean;
    isChildComponent?: boolean;
    invokePath?: string;
    style?: React.CSSProperties;
}

export interface IBaseStates {
    loading: boolean;
    isAuthorized: boolean;
    isAccessDenied?: boolean;
    displayLookupForm?: string;
    windowSize: number;
    hasError?: boolean | null;
}

export interface IBaseComponent {
    //lookupForms: { [id: string]: ReactNode }
    addLookupForm(id: string, lookupForm: React.ReactNode, forceDisplay?: boolean): void;
    destroyLookupForms(id?: string | null): void;
    title: string;
}

export class BaseComponent<TProps extends IBaseProps, TStates extends IBaseStates> extends React.Component<TProps, TStates> implements IBaseComponent {
    static contextType = AppContext;
    title: string;
    constructor(props: TProps) {
        super(props)
        this.title = "";
    }
    mounted: boolean = false;
    private lookupForms: { [id: string]: ReactNode } = {};
    private loadingCount: number = 0;
    setLoading(loading: boolean = true) {
        if (!this.mounted)
            return;
        if (loading)
            this.loadingCount += 1;
        else
            this.loadingCount -= 1;

        if (this.loadingCount === 1)
            this.setState({ loading: true });
        if (this.loadingCount <= 0) {
            this.loadingCount = 0;
            this.setState({ loading: false });
        }
    }
    getPageHeader(props: {
        title: string;
        subTitle?: string;
        onBack?: () => void;
        extraHeaderButtons?: React.ReactNode;
        extraHeaderNodes?: React.ReactNode;
        backNode?: React.ReactNode;
        affix?: boolean;
    }) {
        if (!this.mounted)
            return <></>;

        const header = (
            <PageHeader
                //className="box"
                key={props.title + "-pageheader"}
                ghost={false}
                onBack={props.backNode ? () => { } : async () => { props.onBack && props.onBack(); return Promise.resolve(); }}
                backIcon={props.backNode ? props.backNode : (props.onBack ? <Tooltip title="بازگشت به صفحه قبل" ><Space style={{ color: "darkblue" }}>  <FontAwesomeIcon key="back-icon" className="back-color" icon={faReply} transform={{ flipX: true }} size={'xl'} style={{ margin: 10 }} />{this.props.invokePath && this.props.invokePath}</Space></Tooltip> : undefined)}
                title={props.title}
                subTitle={<span style={{ fontSize: 'medium', color: 'lightblue' }}>{props.subTitle}</span>}
                extra={props.extraHeaderButtons}
                style={{ marginBottom: 10, marginTop: 10, backgroundColor: "rgb(205, 215, 245)" }}
            >
                {props.extraHeaderNodes}
            </PageHeader>
        );
        return (
            props.affix === true ? <Affix className="page-header-affix" offsetTop={0} target={() => document.getElementById("content-container")}>{header}</Affix > : header
        );
    }
    componentDidMount() {
        this.mounted = true;
        window.addEventListener('resize', this.onResize);
    }
    onResize = () => {
        if (!this.mounted)
            return;
        this.setState({ windowSize: window.innerWidth <= 765 ? 1 : 2 });
    }
    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('resize', this.onResize);
        this.destroyLookupForms();
    }

    addLookupForm(id: string, lookupForm: React.ReactNode, forceDisplay: boolean = true/*default forceDisplay is true*/) {
        this.lookupForms[id] = lookupForm;
        if (forceDisplay === true)
            this.setState({ displayLookupForm: id });
    }
    destroyLookupForms(id?: string | null) {
        if (id) {
            delete this.lookupForms[id];
            return;
        }
        for (let key in this.lookupForms)
            delete this.lookupForms[key];
    }
    closeLookupForm() {
        const { displayLookupForm } = this.state;
        if (displayLookupForm)
            delete this.lookupForms[displayLookupForm!];
        this.setState({ displayLookupForm: undefined });
    }
    onLookupClosed = () => {
        this.closeLookupForm();
    }
    async callApi(options: ICallOptions): Promise<boolean> {
        let res: IResult | null = null;
        if (this.mounted) {
            this.setLoading();
        }

        let headers: any = [];
        if (options.headers?.length) {
            for (let i = 0; i < options.headers.length; i++)
                headers.push(options.headers[i]);
        }

        const { setBlockLayout } = this.context as AppContextProps;

        let newOpt: ICallOptions = {
            url: options.url,
            headers: headers,
            data: options.data,
            contentType: options.contentType,
            onSuccess: (result) => {
                res = result;
                if (this.mounted && options.onSuccess) {
                    try {
                        options.onSuccess(result);
                    } catch (error) {
                        if (process.env.NODE_ENV === 'production') {
                            console.error(error);
                            this.setState({ hasError: true });
                        }
                        else
                            throw error;
                    }
                }
                return true;
            },
            onError: (error) => {
                if (this.mounted && options.onError)
                    options.onError(error);
                return false;
            },
            onComplete: () => {
                if (options.withBlock) {
                    setBlockLayout(false);
                }

                if (this.mounted) {

                    if (res) {
                        if (!res.ValidationState.Successful) {
                            if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied)) {
                                Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                                this.setState({ isAccessDenied: true });
                                return;
                            }
                            if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.Unauthorized)) {
                                Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                                this.setState({ isAuthorized: false });
                                return;
                            }
                        }
                        res.ValidationState.ValidationResults.forEach(v => {
                            if (v.Level === enmValidationLevel.Error)
                                Core.errorNotification(v.Message);
                            else if (v.Level === enmValidationLevel.Warning)
                                Core.warningNotification(v.Message);
                            else if (v.Level === enmValidationLevel.Info)
                                Core.successNotification(v.Message);
                        });
                    }

                    if (options.onComplete)
                        options.onComplete();

                    this.setLoading(false);
                }
            }
        };

        if (options.withBlock) {
            setBlockLayout(true, options.withBlockTitle);
        }

        return await Core.callApi(newOpt);
    }
    virtualRender(): ReactNode {
        return (<></>);
    }
    render() {
        if (!this.mounted) {
            return <></>;
        }
        if (!this.state) {
            return (<p> در این کلاس مقدار state تنظیم نشده است </p>);
        }
        const { path, params } = this.context as AppContextProps;
        if (this.state.hasError) {
            let returnUrl = '';
            const url = Core.getUrl(path, params);
            if (url !== '')
                returnUrl = '?returnUrl=' + url;
            let errorPageUrl = '/errorpage';
            return (<Redirect to={{ pathname: errorPageUrl, search: returnUrl }} />);
        }
        if (!this.state.isAuthorized) {
            let returnUrl = '';
            const url = Core.getUrl(path, params);
            if (url !== '')
                returnUrl = '?returnUrl=' + url;
            return (<Redirect to={{ pathname: '/login', search: returnUrl }} />);
        }
        if (this.state.isAccessDenied === true) {
            return (<Redirect to={'/'} />);
        }
        else {
            const lookups: ReactNode[] = [];
            for (let key in this.lookupForms)
                lookups.push(<div key={key} style={{ display: this.state.displayLookupForm === key ? "block" : "none" }}>{this.lookupForms[key]}</div>);
            const displayMainForm = this.state.displayLookupForm ? "none" : "block";

            return (
                <div style={this.props.style}>
                    {lookups}
                    <div style={{ display: displayMainForm }}>{this.virtualRender()}</div>
                </div>
            );
        }
    }
}